import { onMounted, watch, getCurrentInstance, nextTick, inject } from 'vue'
import runAnimations from '@/packages/plugins/animate/runAnimations'

/** 弹窗显隐（由 xy-dialog 的 xy-component provide） */
export const XY_DIALOG_VISIBLE_KEY = Symbol('xyDialogVisible')

/**
 * @param animates 动画配置
 * @param element 节点元数据
 * @param hidenTarget 业务层「是否隐藏」意图（事件 / 数据源只改这个）
 * @param hidenEl 实际驱动 v-show：true = DOM 侧隐藏（含退场过程中保持可见）
 */
export const initComponentAnimate = (animates, element, hidenTarget, hidenEl) => {
  const { proxy } = getCurrentInstance()
  const enterAnimations = animates ? animates.filter((item) => item.isEnter) : []
  const exitAnimations = animates ? animates.filter((item) => !item.isEnter) : []
  let animRunId = 0
  /** 正在跑 CSS 动画链，且与当前链对应的「目标是否隐藏」（用于同向合并，避免重复开跑） */
  let cssTowardHidden = null

  /** 仅在值变化时写 ref，减轻响应式/VNode 抖动 */
  const syncTargetToEl = (hidden) => {
    if (hidenEl.value === hidden) {
      return
    }
    hidenEl.value = hidden
  }

  const runCssAnimations = (list, onComplete) => {
    if (import.meta.env.VITE_APP_TAG !== 'H5') return
    if (!list?.length) {
      onComplete && onComplete()
      return
    }
    animRunId += 1
    const runId = animRunId
    nextTick(() => {
      if (runId !== animRunId) {
        return
      }
      const $el = proxy.$el
      if (!$el?.style) {
        cssTowardHidden = null
        onComplete && onComplete()
        return
      }
      const cssText = $el.style.cssText
      runAnimations($el, list, false, () => {
        if (runId !== animRunId) {
          return
        }
        if ($el.style.cssText !== undefined) {
          $el.style.cssText = cssText
          cssTowardHidden = null
          onComplete && onComplete()
        } else {
          cssTowardHidden = null
        }
      })
    })
  }

  /**
   * 合并同一同步阶段内多次数据源/事件写入，并把「同向重复起动画」压成一次，降低 CPU。
   */
  let flushQueued = false
  const flushVisibility = () => {
    if (import.meta.env.VITE_APP_TAG !== 'H5') {
      syncTargetToEl(!!hidenTarget.value)
      return
    }

    const wantHidden = !!hidenTarget.value
    const hasExit = exitAnimations.length > 0
    const hasEnter = enterAnimations.length > 0

    // 无动画：只同步最终显隐
    if (!hasExit && !hasEnter) {
      if (hidenEl.value === wantHidden) {
        return
      }
      animRunId += 1
      cssTowardHidden = null
      syncTargetToEl(wantHidden)
      return
    }

    // 同一次意图、且已有 CSS 链在跑 → 不重复建 animation / 监听器
    if (cssTowardHidden !== null && cssTowardHidden === wantHidden) {
      return
    }

    // 要隐藏 + 无退场动画
    if (wantHidden && !hasExit) {
      if (hidenEl.value === true) {
        return
      }
      animRunId += 1
      cssTowardHidden = null
      syncTargetToEl(true)
      return
    }

    // 要显示 + 无入场动画
    if (!wantHidden && !hasEnter) {
      if (hidenEl.value === false) {
        return
      }
      animRunId += 1
      cssTowardHidden = null
      syncTargetToEl(false)
      return
    }

    // 已处于目标显隐且没有进行中的「反向」链时，不必再播入场/退场
    if (wantHidden && hasExit && hidenEl.value === true) {
      cssTowardHidden = null
      return
    }
    if (!wantHidden && hasEnter && hidenEl.value === false && cssTowardHidden === null) {
      return
    }

    // 意图与当前链不同：失效旧链，再开新链
    animRunId += 1
    cssTowardHidden = wantHidden

    if (wantHidden && hasExit) {
      syncTargetToEl(false)
      runCssAnimations(exitAnimations, () => {
        if (!hidenTarget.value) {
          return
        }
        syncTargetToEl(true)
      })
      return
    }

    if (!wantHidden && hasEnter) {
      syncTargetToEl(false)
      runCssAnimations(enterAnimations, () => {})
    }
  }

  const scheduleFlush = () => {
    if (flushQueued) {
      return
    }
    flushQueued = true
    queueMicrotask(() => {
      flushQueued = false
      flushVisibility()
    })
  }

  watch(hidenTarget, scheduleFlush)

  const dialogVisible = inject(XY_DIALOG_VISIBLE_KEY, null)

  const playEnterAnimations = () => {
    if (import.meta.env.VITE_APP_TAG !== 'H5') return
    if (!enterAnimations.length || hidenTarget.value) return
    animRunId += 1
    cssTowardHidden = null
    syncTargetToEl(false)
    runCssAnimations(enterAnimations, () => {})
  }

  // 弹窗子节点随页面挂载且父级 v-show 切换，不会 remount；监听弹窗再次打开时重播入场动画
  if (dialogVisible) {
    watch(
      dialogVisible,
      (visible) => {
        if (visible) {
          playEnterAnimations()
        }
      },
      { immediate: true }
    )
  } else {
    onMounted(() => {
      if (import.meta.env.VITE_APP_TAG !== 'H5') return
      if (!element.hiden && enterAnimations.length > 0) {
        runCssAnimations(enterAnimations, () => {})
      }
    })
  }
}
